import { X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { Button } from '@/components/ui/button';

/**
 * Open-window stack so Escape only closes the most recently opened window
 * (e.g. the capture wizard on top of the Screenshots panel).
 */
const openWindows: symbol[] = [];

/**
 * Window geometry, driven by the height.
 *
 * The height fills the space under the viewer header (`top-16` clears the
 * `h-14` header; the 5.5rem accounts for that offset plus a bottom gutter),
 * capped so the window stays a window on tall displays. The width then follows
 * from a fixed ratio instead of its own viewport percentage: on a short or
 * ultra-wide screen a `94vw` window would be far wider than it is tall, leaving
 * the capture floating in a band of empty grey. Narrow screens still clamp to
 * `94vw`, where width — not the ratio — is the binding constraint.
 */
const WINDOW_HEIGHT = 'min(900px, 100dvh - 5.5rem)';
const WINDOW_ASPECT_RATIO = 1.6;
const WINDOW_STYLE: React.CSSProperties = {
    height: WINDOW_HEIGHT,
    width: `min(94vw, calc(${WINDOW_HEIGHT} * ${WINDOW_ASPECT_RATIO}))`,
};

/**
 * Fixed-position window shell shared by every snapshot surface (gallery,
 * capture wizard, annotation editing). Single source of truth for the
 * dimensions and chrome so all snapshot windows are interchangeable:
 * centered below the viewer header, clamped to the viewport on small screens.
 */
interface SnapshotWindowProps {
    icon: LucideIcon;
    title: string;
    /** Rendered next to the title (count badge, step indicator, …). */
    titleAccessory?: React.ReactNode;
    /** Rendered left of the close button. */
    headerActions?: React.ReactNode;
    /** Invoked by the close button and the Escape key. */
    onClose: () => void;
    /** Suppress Escape-to-close (e.g. while a confirm dialog is open). */
    closeDisabled?: boolean;
    children: React.ReactNode;
}

export function SnapshotWindow({
    icon: Icon,
    title,
    titleAccessory,
    headerActions,
    onClose,
    closeDisabled = false,
    children,
}: SnapshotWindowProps) {
    const windowIdRef = useRef<symbol | null>(null);
    if (windowIdRef.current === null) {
        windowIdRef.current = Symbol('snapshot-window');
    }

    useEffect(() => {
        const id = windowIdRef.current!;
        openWindows.push(id);
        return () => {
            const idx = openWindows.indexOf(id);
            if (idx !== -1) openWindows.splice(idx, 1);
        };
    }, []);

    useEffect(() => {
        if (closeDisabled) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && openWindows[openWindows.length - 1] === windowIdRef.current) {
                onClose();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [closeDisabled, onClose]);

    return (
        <div
            style={WINDOW_STYLE}
            className="fixed left-1/2 top-16 z-50 flex -translate-x-1/2 flex-col overflow-hidden rounded-lg border border-border bg-background
        shadow-[0_0_0_1px_rgba(255,255,255,0.06),0_20px_60px_-16px_rgba(0,0,0,0.6),-32px_24px_100px_-24px_rgba(0,0,0,0.2),32px_24px_100px_-24px_rgba(0,0,0,0.2)]"
        >
            <div className="flex h-12 shrink-0 items-center justify-between gap-2 border-b border-border px-3">
                <div className="flex min-w-0 items-center gap-2">
                    <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="truncate text-sm font-medium">{title}</span>
                    {titleAccessory}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                    {headerActions}
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
                        <X className="h-4 w-4" />
                    </Button>
                </div>
            </div>
            {children}
        </div>
    );
}
